<template>
  <div class="app-shell" :class="{ 'sidebar-open': sidebarOpen }">
    <div class="overlay" @click="sidebarOpen = false"></div>

    <aside class="sidebar border-end">
      <div class="sidebar-header d-flex align-items-center gap-2 px-3">
        <div class="brand-icon">
          <i class="bi bi-piggy-bank"></i>
        </div>

        <div class="flex-grow-1">
          <div class="brand-title">GVSG Savings</div>
          <div class="brand-subtitle">Member Panel</div>
        </div>

        <button
          class="btn btn-sm btn-outline-secondary d-lg-none"
          @click="sidebarOpen = false"
          aria-label="Close menu"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <nav class="nav flex-column px-2 mt-2">
        <template v-for="(sec, i) in filteredMenu" :key="i">
          <div
            v-if="sec.section"
            class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold"
          >
            {{ sec.section }}
          </div>

          <template v-for="item in sec.items" :key="item.key || item.to || item.label">
            <!-- Normal item -->
            <RouterLink
              v-if="!item.children || !item.children.length"
              :to="item.to"
              class="nav-link"
              active-class="active"
              exact-active-class="active"
            >
              <i :class="`bi ${item.icon} me-2`"></i>
              <span>{{ item.label }}</span>
            </RouterLink>

            <!-- Parent with submenu -->
            <div v-else class="submenu-wrap">
              <button
                type="button"
                class="nav-link nav-link-btn w-100"
                :class="{ active: isParentActive(item) }"
                @click="toggleSubmenu(item)"
              >
                <span class="d-flex align-items-center">
                  <i :class="`bi ${item.icon} me-2`"></i>
                  <span>{{ item.label }}</span>
                </span>

                <i
                  class="bi submenu-caret"
                  :class="isSubmenuOpen(item) ? 'bi-chevron-up' : 'bi-chevron-down'"
                ></i>
              </button>

              <div v-show="isSubmenuOpen(item)" class="submenu-list">
                <RouterLink
                  v-for="child in item.children"
                  :key="child.to"
                  :to="child.to"
                  class="nav-sublink"
                  active-class="active"
                  exact-active-class="active"
                >
                  <i :class="`bi ${child.icon || 'bi-dot'} me-2`"></i>
                  <span>{{ child.label }}</span>
                </RouterLink>
              </div>
            </div>
          </template>
        </template>

        <!-- Admin entry point -->
        <div
          v-if="isAdmin"
          class="px-2 mt-3 mb-2 text-uppercase small text-muted fw-semibold"
        >
          Admin
        </div>

        <RouterLink
          v-if="isAdmin"
          to="/admin"
          class="nav-link"
          active-class="active"
          exact-active-class="active"
        >
          <i class="bi bi-shield-lock me-2"></i>
          <span>Admin Panel</span>
        </RouterLink>
      </nav>

      <div class="sidebar-footer mt-auto px-3 py-3 border-top">
        <div class="d-flex align-items-center gap-2">
          <div class="avatar">{{ initials }}</div>

          <div class="flex-grow-1">
            <div class="fw-semibold">{{ user?.name || "User" }}</div>
            <div class="text-muted small">{{ user?.role || "-" }}</div>
          </div>

          <button
            class="btn btn-sm btn-outline-secondary"
            @click="doLogout"
            title="Logout"
          >
            <i class="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </aside>

    <div class="main">
      <header class="topbar border-bottom bg-white">
        <div
          class="d-flex align-items-center justify-content-between px-3"
          style="height: 64px"
        >
          <div class="d-flex align-items-center gap-2 gap-md-3">
            <button
              class="btn btn-sm btn-outline-secondary d-inline-flex d-lg-none"
              @click="sidebarOpen = true"
              aria-label="Open menu"
            >
              <i class="bi bi-list"></i>
            </button>

            <div class="fw-semibold text-dark">
              Welcome, {{ user?.name || "..." }}
            </div>

            <span class="badge text-bg-light border">
              {{ user?.role || "member" }}
            </span>
          </div>

          <div class="d-flex align-items-center gap-2">
            <div class="topbar-chip">
              <i class="bi bi-shield-lock me-1"></i> Secure OTP Login
            </div>
          </div>
        </div>
      </header>

      <main class="content p-3 p-md-4">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { MENU_MEMBER } from "@/config/menu";
import { canAccess } from "@/utils/acl";
import { getUser, logout } from "@/services/auth";

export default {
  name: "MemberLayout",

  data() {
    return {
      user: getUser(),
      MENU: MENU_MEMBER,
      sidebarOpen: false,
      openSubmenus: {},
    };
  },

  computed: {
    filteredMenu() {
      const u = this.user;

      return this.MENU
        .filter((sec) => canAccess(u, sec.roles || []))
        .map((sec) => ({
          ...sec,
          items: (sec.items || [])
            .map((item) => {
              const children = (item.children || []).filter((child) =>
                canAccess(u, child.roles || [])
              );

              const allowedSelf =
                !item.roles || item.roles.length === 0 || canAccess(u, item.roles || []);

              // keep parent if itself is allowed OR it has visible children
              if (!allowedSelf && children.length === 0) return null;

              return {
                ...item,
                children,
              };
            })
            .filter(Boolean)
            .filter((item) => {
              if (item.children && item.children.length > 0) return true;
              return !!item.to;
            }),
        }))
        .filter((sec) => sec.items.length > 0);
    },

    initials() {
      const n = this.user?.name || "";
      const parts = n.trim().split(" ").filter(Boolean);
      const a = parts[0]?.[0] || "U";
      const b = parts[1]?.[0] || "";
      return (a + b).toUpperCase();
    },

    isAdmin() {
      return ["admin", "treasurer"].includes(this.user?.role);
    },
  },

  watch: {
    $route() {
      this.user = getUser();
      this.sidebarOpen = false;
      this.expandActiveParents();
    },
  },

  mounted() {
    this.user = getUser();
    this.expandActiveParents();
  },

  methods: {
    submenuKey(item) {
      return item.key || item.label || item.to;
    },

    isSubmenuOpen(item) {
      return !!this.openSubmenus[this.submenuKey(item)];
    },

    toggleSubmenu(item) {
      const key = this.submenuKey(item);
      this.openSubmenus = {
        ...this.openSubmenus,
        [key]: !this.openSubmenus[key],
      };
    },

    isParentActive(item) {
      if (!item.children || !item.children.length) return false;
      return item.children.some((child) => this.$route.path.startsWith(child.to));
    },

    expandActiveParents() {
      const next = { ...this.openSubmenus };

      this.filteredMenu.forEach((sec) => {
        (sec.items || []).forEach((item) => {
          if (item.children && item.children.length) {
            const key = this.submenuKey(item);
            if (item.children.some((child) => this.$route.path.startsWith(child.to))) {
              next[key] = true;
            }
          }
        });
      });

      this.openSubmenus = next;
    },

    async doLogout() {
      logout();
      this.$router.push("/login/sms");
    },
  },
};
</script>

<style scoped>
.app-shell {
  --brand-1: #0d6efd;
  --brand-2: #6f42c1;
  --brand-3: #20c997;

  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: #f6f7fb;
}

.overlay {
  display: none;
}

.sidebar {
  display: flex;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.92);
  background: linear-gradient(160deg, var(--brand-2), var(--brand-1));
  border-right: 0 !important;
}

.sidebar-header {
  height: 72px;
}

.brand-title {
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
}

.brand-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
}

.brand-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 20px;
}

.sidebar .text-muted {
  color: rgba(255, 255, 255, 0.72) !important;
}

.nav-link,
.nav-link-btn {
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.88);
  padding: 10px 12px;
  margin: 4px 6px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  transition: background 0.15s ease, transform 0.15s ease;
  text-decoration: none;
}

.nav-link-btn {
  width: calc(100% - 12px);
  margin: 4px 6px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.88);
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: background 0.15s ease, transform 0.15s ease;
  text-decoration: none;
  border: 0;
  background: transparent;
}

.nav-link:hover,
.nav-link-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transform: translateX(2px);
}

.nav-link.active,
.nav-link-btn.active {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-weight: 800;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.submenu-wrap {
  margin-bottom: 2px;
}

.submenu-list {
  margin: 2px 6px 8px 18px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.18);
}

.nav-sublink {
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.82);
  padding: 8px 10px;
  margin: 3px 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-decoration: none;
  font-size: 14px;
  transition: background 0.15s ease, transform 0.15s ease;
}

.nav-sublink:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  transform: translateX(2px);
}

.nav-sublink.active {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-weight: 700;
}

.submenu-caret {
  font-size: 12px;
  opacity: 0.9;
}

.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.16) !important;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  font-weight: 900;
}

.sidebar .btn-outline-secondary {
  color: rgba(255, 255, 255, 0.9);
  border-color: rgba(255, 255, 255, 0.28);
}

.sidebar .btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
}

.main {
  display: flex;
  flex-direction: column;
}

.topbar {
  border-bottom: 0 !important;
  background: linear-gradient(90deg, var(--brand-1), var(--brand-3)) !important;
  color: #fff;
}

.topbar .text-dark {
  color: #fff !important;
}

.topbar .badge.text-bg-light.border {
  background: rgba(255, 255, 255, 0.16) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
  color: #fff !important;
}

.topbar-chip {
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 13px;
  color: #fff;
}

.topbar .btn-outline-secondary {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.28);
}

.topbar .btn-outline-secondary:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.35);
}

.content {
  min-height: calc(100vh - 64px);
}

@media (max-width: 991.98px) {
  .app-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 240px;
    z-index: 1055;
    transform: translateX(-102%);
    transition: transform 0.2s ease;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  .app-shell.sidebar-open .sidebar {
    transform: translateX(0);
  }

  .overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1050;
    display: none;
  }

  .app-shell.sidebar-open .overlay {
    display: block;
  }
}
</style>